import { Component } from "react";
import { Button, Text } from "@chakra-ui/react";

interface CountdownTimerProps {
  expiresAt: string | Date;
  createdAt: string | Date;
  onExpired?: () => void;
  onResend?: () => void;
  showSeconds?: boolean; 
  warningThreshold?: number; 
  resendDelaySeconds?: number; 
}

interface CountdownTimerState {
  timeRemaining: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  };
  resendTimeRemaining: number; 
  isExpired: boolean;
  isWarning: boolean;
  totalMinutesRemaining: number;
}

export default class CountdownTimer extends Component<
  CountdownTimerProps,
  CountdownTimerState
> {
  private intervalId: NodeJS.Timeout | null = null;

  constructor(props: CountdownTimerProps) {
    super(props);
    this.state = {
      timeRemaining: { days: 0, hours: 0, minutes: 0, seconds: 0 },
      resendTimeRemaining: 0,
      isExpired: false,
      isWarning: false,
      totalMinutesRemaining: 0,
    };
  }

  componentDidMount() {
    this.calculateTimeRemaining();
    this.startTimer();
  }

  componentDidUpdate(prevProps: CountdownTimerProps) {
    if (
      prevProps.expiresAt !== this.props.expiresAt ||
      prevProps.createdAt !== this.props.createdAt
    ) {
      this.calculateTimeRemaining();
    }
  }

  componentWillUnmount() {
    this.stopTimer();
  }

  private startTimer = () => {
    this.stopTimer();
    this.intervalId = setInterval(() => {
      this.calculateTimeRemaining();
    }, 1000);
  };

  private stopTimer = () => {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  };

  private calculateTimeRemaining = () => {
    const now = Date.now();
    const expirationTime = new Date(this.props.expiresAt).getTime();
    const createdTime = new Date(this.props.createdAt).getTime();
    const resendDelay =
      (this.props.resendDelaySeconds || 120) * 1000; // default 2 minutes

    // --- OTP Expiration ---
    const timeDifference = expirationTime - now;

    let isExpired = false;
    let isWarning = false;
    let totalMinutesRemaining = 0;
    let timeRemaining = { days: 0, hours: 0, minutes: 0, seconds: 0 };

    if (timeDifference > 0) {
      const days = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (timeDifference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const minutes = Math.floor(
        (timeDifference % (1000 * 60 * 60)) / (1000 * 60)
      );
      const seconds = Math.floor((timeDifference % (1000 * 60)) / 1000);

      totalMinutesRemaining = Math.floor(timeDifference / (1000 * 60));
      const warningThreshold = this.props.warningThreshold || 5;

      timeRemaining = { days, hours, minutes, seconds };
      isExpired = false;
      isWarning =
        totalMinutesRemaining <= warningThreshold && totalMinutesRemaining > 0;
    } else {
      isExpired = true;
    }

    // --- Resend Timer ---
    const resendAvailableAt = createdTime + resendDelay;
    const resendTimeRemaining = Math.max(
      0,
      Math.floor((resendAvailableAt - now) / 1000)
    );

    if (isExpired && this.props.onExpired) {
      this.props.onExpired();
    }

    this.setState({
      timeRemaining,
      resendTimeRemaining,
      isExpired,
      isWarning,
      totalMinutesRemaining,
    });
  };

  private formatTimeComponent = (value: number): string => {
    return value.toString().padStart(2, "0");
  };

  private renderTimeDisplay = (): string => {
    const { days, hours, minutes, seconds } = this.state.timeRemaining;
    const { showSeconds = true } = this.props;

    if (days > 0) {
      return `${days}d ${this.formatTimeComponent(hours)}h ${this.formatTimeComponent(
        minutes
      )}m${showSeconds ? ` ${this.formatTimeComponent(seconds)}s` : ""}`;
    } else if (hours > 0) {
      return `${this.formatTimeComponent(hours)}h ${this.formatTimeComponent(
        minutes
      )}m${showSeconds ? ` ${this.formatTimeComponent(seconds)}s` : ""}`;
    } else {
      return `${this.formatTimeComponent(minutes)}m${
        showSeconds ? ` ${this.formatTimeComponent(seconds)}s` : ""
      }`;
    }
  };

  private renderResendLabel = (): string => {
    const { resendTimeRemaining } = this.state;

    if (resendTimeRemaining > 0) {
      const minutes = Math.floor(resendTimeRemaining / 60);
      const seconds = resendTimeRemaining % 60;
      return `Resend OTP (${this.formatTimeComponent(
        minutes
      )}:${this.formatTimeComponent(seconds)})`;
    }
    return "Resend OTP";
  };

  render() {
    const { resendTimeRemaining } = this.state;

    return (
      <>
        <Button
          type="button"
          onClick={this.props.onResend}
          marginTop={4}
          isDisabled={resendTimeRemaining > 0}
        >
          {this.renderResendLabel()}
        </Button>
      </>
    );
  }
}
